/**
 * First-party cookie consent preferences (GDPR/ePrivacy hygiene).
 * Stored in localStorage — not an httpOnly cookie.
 */

export const COOKIE_CONSENT_STORAGE_KEY = 'millet_cookie_consent';

/** Bump when categories or legal meaning change so the banner reappears. */
export const COOKIE_CONSENT_VERSION = 1;

export const COOKIE_CONSENT_CHANGED_EVENT = 'millet:cookie-consent-changed';
export const OPEN_COOKIE_SETTINGS_EVENT = 'millet:open-cookie-settings';

export const CONSENT_CATEGORIES = {
  necessary: 'necessary',
  functional: 'functional',
  analytics: 'analytics',
  marketing: 'marketing',
};

const OPTIONAL_CATEGORIES = [
  CONSENT_CATEGORIES.functional,
  CONSENT_CATEGORIES.analytics,
  CONSENT_CATEGORIES.marketing,
];

function emitConsentChanged(consent) {
  if (typeof window === 'undefined') return;
  window.dispatchEvent(
    new CustomEvent(COOKIE_CONSENT_CHANGED_EVENT, { detail: consent }),
  );
}

function buildConsent(partial = {}) {
  return {
    version: COOKIE_CONSENT_VERSION,
    necessary: true,
    functional: Boolean(partial.functional),
    analytics: Boolean(partial.analytics),
    marketing: Boolean(partial.marketing),
    updatedAt: new Date().toISOString(),
  };
}

function isValidConsent(value) {
  if (!value || typeof value !== 'object') return false;
  if (value.version !== COOKIE_CONSENT_VERSION) return false;
  if (value.necessary !== true) return false;
  return OPTIONAL_CATEGORIES.every((key) => typeof value[key] === 'boolean');
}

export function getConsent() {
  if (typeof window === 'undefined') return null;
  try {
    const raw = window.localStorage.getItem(COOKIE_CONSENT_STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    return isValidConsent(parsed) ? parsed : null;
  } catch {
    return null;
  }
}

export function hasStoredConsent() {
  return getConsent() !== null;
}

export function hasConsent(category) {
  if (category === CONSENT_CATEGORIES.necessary) return true;
  const consent = getConsent();
  if (!consent) return false;
  return Boolean(consent[category]);
}

export function saveConsent(partial = {}) {
  const current = getConsent() || {};
  const next = buildConsent({
    functional: partial.functional ?? current.functional ?? false,
    analytics: partial.analytics ?? current.analytics ?? false,
    marketing: partial.marketing ?? current.marketing ?? false,
  });

  if (typeof window !== 'undefined') {
    window.localStorage.setItem(COOKIE_CONSENT_STORAGE_KEY, JSON.stringify(next));
  }
  emitConsentChanged(next);
  return next;
}

export function acceptAll() {
  return saveConsent({
    functional: true,
    analytics: true,
    marketing: true,
  });
}

export function rejectAll() {
  return saveConsent({
    functional: false,
    analytics: false,
    marketing: false,
  });
}

export function dispatchOpenCookieSettings() {
  if (typeof window === 'undefined') return;
  window.dispatchEvent(new CustomEvent(OPEN_COOKIE_SETTINGS_EVENT));
}
